import { useTranslation } from 'react-i18next';
import { useMedia } from '../contexts/MediaContext';
import { LazyVideo } from './LazyVideo';
import { getAssetUrls } from '../utils/media';

interface PercorsoItem {
  category: string;
  title: string;
  description: string;
  features: string[];
}

export const Percorsi = () => {
  const { t } = useTranslation();
  const { getFirstByPlacement } = useMedia();
  const items = t('percorsi.items', { returnObjects: true }) as PercorsoItem[];

  const scrollToForm = () => {
    document.getElementById('form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="percorsi" className="py-14 animate-section bg-[linear-gradient(135deg,rgba(40,80,245,0.12)_0%,rgba(91,63,246,0.12)_45%,rgba(192,60,247,0.16)_100%)] dark:bg-[rgba(15,18,48,0.9)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="uppercase tracking-widest text-xs font-semibold text-regai-strong mb-2">
            {t('percorsi.eyebrow')}
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold leading-tight">
            {t('percorsi.title')}
          </h2>
          <p className="mt-3 text-regai-muted text-lg">{t('percorsi.subtitle')}</p>
        </div>

        {/* Media placement: percorsi.visual_journey */}
        {(() => {
          const asset = getFirstByPlacement('percorsi.visual_journey');
          const urls = getAssetUrls(asset ?? undefined);
          if (!asset) return null;
          return (
            <div className="mt-8 overflow-hidden rounded-3xl border border-regai bg-regai-surface">
              {asset.type === 'video' ? (
                <div className="relative aspect-video">
                  <LazyVideo
                    src={urls.mediaUrl}
                    poster={urls.thumbnailUrl}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </div>
              ) : (
                <img src={urls.mediaUrl} alt={asset.title} className="w-full object-cover" />
              )}
            </div>
          );
        })()}

        <div className="mt-10 grid lg:grid-cols-3 gap-6 card-group">
          {items.map((item, idx) => (
            <div key={idx} className="card bg-[var(--regai-white)]/90 dark:bg-[var(--regai-neutral-900)]/80">
              <div className="text-sm font-semibold text-regai">{item.category}</div>
              <h3 className="text-xl font-bold mt-1" role="heading" aria-level={3}>{item.title}</h3>
              <p className="text-regai-muted mt-2">{item.description}</p>

              <ul className="mt-4 space-y-2" role="list">
                {item.features.map((feature, fidx) => (
                  <li key={fidx} className="flex gap-3 items-start">
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={scrollToForm}
                className="mt-5 inline-flex rounded-xl border border-transparent px-4 py-2 text-sm font-semibold btn-regai"
              >
                {t('percorsi.cta')}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
